// --- Dependencies --------------------------------------------------------------------------------
import { useEffect, useState } from "react";
// --- Our Modules --------------------------------------------------------------------------------
import useFetch from "@/hooks/useFetch";
import usePermission from "@/hooks/usePermission";
import usePopup from "@/hooks/usePopup";
import useTranslation from "@/hooks/useTranslation";
import type { TyApiResponse, TyClient } from "@/types";
import ClientCard from "@/UI/cards/ClientCard";
import Skeleton from "@/UI/atoms/Skeleton";
import ActionsBar from "@/UI/components/ActionsBar";
import ConfirmPopup from "@/UI/popups/ConfirmPopup";
import EditClientPermissionsPopup from "@/UI/popups/EditClientPermissionsPopup";
import FormClientPopup from "@/UI/popups/FormClientPopup";
import MoreActionsPopup from "@/UI/popups/MoreActionsPopup";
import "./styles.css";

// --- Logic --------------------------------------------------------------------------------------

export default function ClientsScreen() {
    const t = useTranslation();
    const { addComp, removeComp } = usePopup();
    const { canEdit } = usePermission();

    const { data, loading, error, invoke } = useFetch<TyApiResponse<{ clients: TyClient[]; total: number }>>("/clients/", { query: { limit: 100 } });

    const clients = data?.data?.clients ?? [];

    // --- Delete ----------------------------------------------------------------------------------

    const [deletingId, setDeletingId] = useState<string | null>(null);

    const { data: deleteData, invoke: invokeDelete } = useFetch<TyApiResponse<Record<string, never>>>(
        `/clients/${deletingId ?? "x"}`,
        { method: "DELETE" },
        false
    );

    useEffect(() => {
        if (!deletingId) return;
        invokeDelete();
    }, [deletingId]);

    useEffect(() => {
        if (!deleteData?.success) return;
        setDeletingId(null);
        invoke();
    }, [deleteData]);

    // --- Handlers --------------------------------------------------------------------------------

    const handleAdd = () => {
        addComp({
            name: "form-client",
            Component: FormClientPopup,
            place: "end",
            anim: "endSlide",
            cover: "shaded",
            coverClose: true,
            details: { onSuccess: invoke },
        });
    };

    const handleEdit = (client: TyClient) => {
        addComp({
            name: "edit-client-permissions",
            Component: EditClientPermissionsPopup,
            place: "center",
            anim: "scale",
            cover: "shaded",
            coverClose: true,
            details: { client, onSuccess: invoke },
        });
    };

    const handleDeletePrompt = (client: TyClient) => {
        removeComp("confirm-delete-client");
        addComp({
            name: "confirm-delete-client",
            Component: ConfirmPopup,
            place: "center",
            anim: "scale",
            cover: "shaded",
            details: {
                message: t.formClient.deleteMessage,
                icon: "/icons/delete-icon.svg",
                variant: "danger",
                confirmLabel: t.formClient.deleteConfirm,
                onConfirm: () => setDeletingId(client._id),
            },
        });
    };

    const handleMore = (client: TyClient, e: React.MouseEvent<HTMLButtonElement>) => {
        const rect = e.currentTarget.getBoundingClientRect();
        const name = `more-actions-${client._id}`;
        removeComp(name);
        addComp({
            name,
            Component: MoreActionsPopup,
            place: "anchor-below",
            anim: "scaleTop",
            cover: "invisible",
            coverClose: true,
            anchor: { top: rect.top, left: rect.left, right: rect.right, bottom: rect.bottom, width: rect.width, height: rect.height },
            details: {
                actions: [
                    { label: t.formClient.editAction, icon: "/icons/edit-icon.svg", handler: () => handleEdit(client) },
                    { label: t.formClient.deleteAction, icon: "/icons/delete-icon.svg", handler: () => handleDeletePrompt(client), danger: true },
                ],
            },
        });
    };

    // --- Render ----------------------------------------------------------------------------------

    return (
        <div className="clients-screen">
            <ActionsBar title={t.clients.title} addLabel={t.clients.addLabel} onAdd={canEdit("clients") ? handleAdd : undefined} />
            <div className="clients-screen-content">
                {loading && (
                    <div className="clients-screen-grid">
                        {Array.from({ length: 4 }).map((_, i) => (
                            <Skeleton key={i} className="clients-screen-skeleton" />
                        ))}
                    </div>
                )}
                {error && <p className="clients-screen-message clients-screen-message--error">{error}</p>}
                {!loading && !error && clients.length === 0 && (
                    <div className="clients-screen-empty">
                        <div className="clients-screen-empty-icon">
                            <img src="/icons/profile-icon.svg" alt="" width={32} height={32} />
                        </div>
                        <div className="clients-screen-empty-text">
                            <p className="clients-screen-empty-title">{t.clients.emptyTitle}</p>
                            <p className="clients-screen-empty-subtitle">{t.clients.emptySubtitle}</p>
                        </div>
                    </div>
                )}
                {!loading && !error && clients.length > 0 && (
                    <div className="clients-screen-grid">
                        {clients.map((client) => (
                            <ClientCard key={client._id} client={client} onMore={canEdit("clients") ? (e) => handleMore(client, e) : undefined} />
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}
